import { supabase } from './supabase';

// =============================================
// TYPES
// =============================================

// KALTAKQUISE Status (muss mit DB lead_status enum übereinstimmen)
export type LeadStatus = 
  | 'neu' 
  | 'kontaktiert'
  | 'erstgespraech_geplant' 
  | 'interessiert' 
  | 'nicht_interessiert' 
  | 'spaeter' 
  | 'gewonnen';

// ANFRAGEN Status (Vertriebspipeline)
export type AnfrageStatus = 
  | 'neu' 
  | 'erstgespraech_geplant' 
  | 'angebot_in_arbeit' 
  | 'angebot_versendet' 
  | 'verkaufsgespraech' 
  | 'nachfassen' 
  | 'gewonnen' 
  | 'verloren';

export interface CrmLead {
  id: string;
  firma: string;
  ansprechpartner: string | null;
  email: string | null;
  telefon: string | null;
  website: string | null;
  branche: string | null;
  status: LeadStatus;
  notizen: string | null;
  naechster_kontakt: string | null;
  created_at: string;
  updated_at: string;
  // Verknüpfung nach Konvertierung
  customer_id?: string | null;
}

export interface CrmAnfrage {
  id: string;
  firma: string;
  ansprechpartner: string | null;
  email: string | null;
  telefon: string | null;
  quelle: string | null;
  interesse: string | null;
  position: string | null; // Neu: Position (Geschäftsführer, Marketing, etc.)
  herausforderung: string | null; // Neu: Größte Herausforderung
  status: AnfrageStatus;
  notizen: string | null;
  angebot_datum: string | null;
  nachfass_datum: string | null;
  lead_id: string | null;
  created_at: string;
  updated_at: string;
  // Verknüpfung nach Konvertierung
  customer_id?: string | null;
}

export interface CrmAufgabe {
  id: string;
  customer_id: string | null;
  project_id: string | null;
  titel: string;
  beschreibung: string | null;
  erledigt: boolean;
  deadline: string | null;
  prioritaet: 'niedrig' | 'mittel' | 'hoch' | null;
  sortierung: number;
  created_at: string;
  updated_at: string;
  // Joined data
  customer?: { id: string; name: string };
  project?: { id: string; name: string };
}

export interface AufgabenPaketAufgabe {
  titel: string;
  tage_bis_deadline?: number | null;
}

export interface AufgabenPaket {
  id: string;
  name: string;
  beschreibung: string | null;
  aufgaben: AufgabenPaketAufgabe[];
  created_at?: string;
}

export interface AufgabenVorlage {
  id: string;
  paket_id: string;
  titel: string;
  beschreibung: string | null;
  tage_bis_deadline: number | null;
  sortierung: number;
}

// =============================================
// LEADS API
// =============================================

export async function getLeads(): Promise<CrmLead[]> {
  const { data, error } = await supabase
    .from('crm_leads')
    .select('*')
    .order('created_at', { ascending: false });
  
  if (error) throw error;
  return data || [];
}

export async function createLead(lead: Partial<CrmLead>): Promise<CrmLead> {
  const { data, error } = await supabase
    .from('crm_leads')
    .insert(lead)
    .select()
    .single();
  
  if (error) throw error;
  return data;
}

export async function updateLead(id: string, updates: Partial<CrmLead>): Promise<CrmLead> {
  console.log('📡 API updateLead called:', { id, updates });
  const { data, error } = await supabase
    .from('crm_leads')
    .update(updates)
    .eq('id', id)
    .select()
    .single();
  
  if (error) {
    console.error('📡 API updateLead error:', error);
    throw error;
  }
  console.log('📡 API updateLead success:', data);
  return data;
}

export async function deleteLead(id: string): Promise<void> {
  const { error } = await supabase
    .from('crm_leads')
    .delete()
    .eq('id', id);
  
  if (error) throw error;
}

// Lead zu Anfrage konvertieren
export async function leadZuAnfrage(leadId: string): Promise<CrmAnfrage> {
  const { data: lead, error: leadError } = await supabase
    .from('crm_leads')
    .select('*')
    .eq('id', leadId)
    .single();
  
  if (leadError) throw leadError;

  const { data: anfrage, error: anfrageError } = await supabase
    .from('crm_anfragen')
    .insert({
      firma: lead.firma,
      ansprechpartner: lead.ansprechpartner,
      email: lead.email,
      telefon: lead.telefon,
      notizen: lead.notizen,
      lead_id: leadId,
      quelle: 'Kaltakquise',
    })
    .select()
    .single();
  
  if (anfrageError) throw anfrageError;

  // Lead-Status aktualisieren
  await supabase.from('crm_leads').update({ status: 'interessiert' as LeadStatus }).eq('id', leadId);

  return anfrage;
}

// Lead direkt zum Kunden konvertieren (gewonnen)
export async function leadZuKunde(leadId: string): Promise<{ customer_id: string }> {
  const { data: lead, error: leadError } = await supabase
    .from('crm_leads')
    .select('*')
    .eq('id', leadId)
    .single();
  
  if (leadError) throw leadError;

  // Slug generieren
  const slug = lead.firma.toLowerCase()
    .replace(/[äöüß]/g, (c: string) => (({ 'ä': 'ae', 'ö': 'oe', 'ü': 'ue', 'ß': 'ss' } as Record<string, string>)[c] || c))
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '');

  // Kunde anlegen (mit korrekten Spaltennamen)
  const { data: customer, error: customerError } = await supabase
    .from('customers')
    .insert({
      name: `${lead.firma}${lead.ansprechpartner ? ' - ' + lead.ansprechpartner : ''}`,
      slug: slug + '-' + Date.now().toString(36),
      forward_email: lead.email,
      active: true,
    })
    .select()
    .single();
  
  if (customerError) throw customerError;

  // Lead-Status aktualisieren
  await supabase.from('crm_leads').update({ status: 'gewonnen' }).eq('id', leadId);

  return { customer_id: customer.id };
}

// =============================================
// ANFRAGEN API
// =============================================

export async function getAnfragen(): Promise<CrmAnfrage[]> {
  const { data, error } = await supabase
    .from('crm_anfragen')
    .select('*')
    .order('created_at', { ascending: false });
  
  if (error) throw error;
  return data || [];
}

export async function createAnfrage(anfrage: Partial<CrmAnfrage>): Promise<CrmAnfrage> {
  const { data, error } = await supabase
    .from('crm_anfragen')
    .insert(anfrage)
    .select()
    .single();
  
  if (error) throw error;
  return data;
}

export async function updateAnfrage(id: string, updates: Partial<CrmAnfrage>): Promise<CrmAnfrage> {
  console.log('📡 API updateAnfrage called:', { id, updates });
  const { data, error } = await supabase
    .from('crm_anfragen')
    .update(updates)
    .eq('id', id)
    .select()
    .single();
  
  if (error) {
    console.error('📡 API updateAnfrage error:', error);
    throw error;
  }
  console.log('📡 API updateAnfrage success:', data);
  return data;
}

export async function deleteAnfrage(id: string): Promise<void> {
  const { error } = await supabase
    .from('crm_anfragen')
    .delete()
    .eq('id', id);
  
  if (error) throw error;
}

// Anfrage zum Kunden konvertieren (gewonnen)
export async function anfrageZuKunde(anfrageId: string): Promise<{ customer_id: string }> {
  const { data: anfrage, error: anfrageError } = await supabase
    .from('crm_anfragen')
    .select('*')
    .eq('id', anfrageId)
    .single();
  
  if (anfrageError) throw anfrageError;

  // Slug generieren
  const slug = anfrage.firma.toLowerCase()
    .replace(/[äöüß]/g, (c: string) => (({ 'ä': 'ae', 'ö': 'oe', 'ü': 'ue', 'ß': 'ss' } as Record<string, string>)[c] || c))
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '');

  // Kunde anlegen (mit korrekten Spaltennamen)
  const { data: customer, error: customerError } = await supabase
    .from('customers')
    .insert({
      name: `${anfrage.firma}${anfrage.ansprechpartner ? ' - ' + anfrage.ansprechpartner : ''}`,
      slug: slug + '-' + Date.now().toString(36),
      forward_email: anfrage.email,
      active: true,
    })
    .select()
    .single();
  
  if (customerError) throw customerError;

  // Anfrage-Status aktualisieren
  await supabase.from('crm_anfragen').update({ status: 'gewonnen' }).eq('id', anfrageId);

  // Wenn von Lead kam, auch Lead aktualisieren
  if (anfrage.lead_id) {
    await supabase.from('crm_leads').update({ status: 'gewonnen' }).eq('id', anfrage.lead_id);
  }

  return { customer_id: customer.id };
}

// =============================================
// AUFGABEN API (direkt an Kunden/Projekte)
// =============================================

export async function getAufgaben(filters?: { customer_id?: string; project_id?: string; erledigt?: boolean }): Promise<CrmAufgabe[]> {
  let query = supabase
    .from('crm_aufgaben')
    .select(`
      *,
      customer:customers(id, name),
      project:projects(id, name)
    `)
    .order('deadline', { ascending: true, nullsFirst: false })
    .order('sortierung', { ascending: true });

  if (filters?.customer_id) {
    query = query.eq('customer_id', filters.customer_id);
  }
  if (filters?.project_id) {
    query = query.eq('project_id', filters.project_id);
  }
  if (filters?.erledigt !== undefined) {
    query = query.eq('erledigt', filters.erledigt);
  }

  const { data, error } = await query;
  if (error) throw error;
  return data || [];
}

export async function createAufgabe(aufgabe: Partial<CrmAufgabe>): Promise<CrmAufgabe> {
  const { data, error } = await supabase
    .from('crm_aufgaben')
    .insert(aufgabe)
    .select()
    .single();
  
  if (error) throw error;
  return data;
}

export async function updateAufgabe(id: string, updates: Partial<CrmAufgabe>): Promise<CrmAufgabe> {
  const { data, error } = await supabase
    .from('crm_aufgaben')
    .update(updates)
    .eq('id', id)
    .select()
    .single();
  
  if (error) throw error;
  return data;
}

export async function deleteAufgabe(id: string): Promise<void> {
  const { error } = await supabase
    .from('crm_aufgaben')
    .delete()
    .eq('id', id);
  
  if (error) throw error;
}

export async function toggleAufgabe(id: string, erledigt: boolean): Promise<CrmAufgabe> {
  return updateAufgabe(id, { erledigt });
}

// =============================================
// PAKETE API
// =============================================

export async function getAufgabenPakete(): Promise<AufgabenPaket[]> {
  const { data, error } = await supabase
    .from('crm_aufgaben_pakete')
    .select(`
      *,
      vorlagen:crm_aufgaben_vorlagen(*)
    `)
    .order('name');
  
  if (error) throw error;
  return data || [];
}

// Paket auf Kunde/Projekt anwenden
export async function paketAnwenden(paketId: string, customerId: string, projectId?: string): Promise<CrmAufgabe[]> {
  // Hole das Paket mit den aktuellen Aufgaben
  const { data: paket, error: paketError } = await supabase
    .from('crm_aufgaben_pakete')
    .select('*')
    .eq('id', paketId)
    .single();
  
  if (paketError) throw paketError;
  if (!paket || !paket.aufgaben || paket.aufgaben.length === 0) {
    throw new Error('Paket hat keine Aufgaben');
  }

  // Erstelle Aufgaben aus dem Aufgaben-Array
  const heute = new Date();
  const aufgaben = (paket.aufgaben as AufgabenPaketAufgabe[]).map((aufgabe, index) => ({
    customer_id: customerId,
    project_id: projectId || null,
    titel: aufgabe.titel,
    beschreibung: null,
    deadline: aufgabe.tage_bis_deadline 
      ? new Date(heute.getTime() + aufgabe.tage_bis_deadline * 24 * 60 * 60 * 1000).toISOString().split('T')[0]
      : null,
    prioritaet: 'mittel' as const,
    sortierung: index,
    erledigt: false,
  }));

  const { data, error } = await supabase
    .from('crm_aufgaben')
    .insert(aufgaben)
    .select();
  
  if (error) throw error;
  return data || [];
}

// =============================================
// KUNDEN & PROJEKTE (für To-Do Seite)
// =============================================

export async function getKundenMitProjekten() {
  const { data, error } = await supabase
    .from('customers')
    .select(`
      id, name, slug, forward_email, active,
      projects:projects(id, name, is_active)
    `)
    .eq('active', true)
    .order('name');
  
  if (error) throw error;
  return data || [];
}

export async function getProjekte() {
  const { data, error } = await supabase
    .from('projects')
    .select(`
      id, name, is_active,
      customer:customers(id, name)
    `)
    .order('name');
  
  if (error) throw error;
  return data || [];
}
